import type { TakeoffKind, TakeoffScale, TakeoffUnit } from '@buildbee/contracts';

/**
 * On-plan takeoff maths (pure, shared by the API and the browser).
 *
 * Coordinates are sheet pixels at render scale 1: for a PDF page that is PDF points (72 per inch
 * of paper), with the page's own /Rotate applied; for an image it is the image's natural pixels.
 * A sheet's scale says how many real-world units one of those pixels is (`unitsPerPixel`, in the
 * scale's `unit`). Quantities are computed from geometry x scale (x multiplier) x (1 + waste) and
 * expressed in an estimating unit (lf, sf, sy, cy, ea, m, m2, m3, ...).
 *
 * The server always recomputes quantities with these functions; the client uses the same code for
 * live previews, so what the estimator sees is what is stored.
 */

export type TakeoffPoint = { x: number; y: number };
export type TakeoffShapes = readonly (readonly TakeoffPoint[])[];

/** PDF user space: 72 points per inch, which is what pdf.js renders at scale 1. */
export const PDF_POINTS_PER_INCH = 72;

// ---------------------------------------------------------------------------
// Geometry in pixels
// ---------------------------------------------------------------------------

function dist(a: TakeoffPoint, b: TakeoffPoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/** Length of an open polyline (sum of its segments). Fewer than two points is 0. */
export function polylineLength(points: readonly TakeoffPoint[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) total += dist(points[i - 1]!, points[i]!);
  return total;
}

/**
 * Area of a simple polygon by the shoelace formula (the closing edge is implied). Orientation does
 * not matter; fewer than three points is 0.
 */
export function polygonArea(points: readonly TakeoffPoint[]): number {
  if (points.length < 3) return 0;
  let twice = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % points.length]!;
    twice += a.x * b.y - b.x * a.y;
  }
  return Math.abs(twice) / 2;
}

/** Perimeter of a closed polygon (useful for an area's edge, e.g. base trim around a room). */
export function polygonPerimeter(points: readonly TakeoffPoint[]): number {
  if (points.length < 2) return 0;
  return polylineLength(points) + (points.length > 2 ? dist(points[points.length - 1]!, points[0]!) : 0);
}

/** Number of counted points over every shape. */
export function countPoints(shapes: TakeoffShapes): number {
  return shapes.reduce((n, s) => n + s.length, 0);
}

/**
 * The raw pixel measure of a measurement: total polyline length (px) for linear, total polygon
 * area (px²) for area (several shapes are summed), number of points for count.
 */
export function geometryPixels(kind: TakeoffKind, shapes: TakeoffShapes): number {
  switch (kind) {
    case 'linear':
      return shapes.reduce((n, s) => n + polylineLength(s), 0);
    case 'area':
      return shapes.reduce((n, s) => n + polygonArea(s), 0);
    case 'count':
      return countPoints(shapes);
  }
}

/** Why a geometry cannot be measured, or null when it is fine. */
export function geometryProblem(kind: TakeoffKind, shapes: TakeoffShapes): string | null {
  if (shapes.length === 0) return 'Draw at least one shape';
  for (const s of shapes) {
    for (const p of s) if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) return 'Points must be numbers';
    if (kind === 'linear' && s.length < 2) return 'A line needs at least two points';
    if (kind === 'area' && s.length < 3) return 'An area needs at least three points';
    if (kind === 'count' && s.length < 1) return 'Click at least one point to count';
  }
  return null;
}

// ---------------------------------------------------------------------------
// Units
// ---------------------------------------------------------------------------

/** Metres per scale unit. */
export const METERS_PER_UNIT: Record<TakeoffUnit, number> = { ft: 0.3048, in: 0.0254, yd: 0.9144, m: 1, mm: 0.001 };

/** 0 = count, 1 = length, 2 = area, 3 = volume. */
export type TakeoffDimension = 0 | 1 | 2 | 3;

type OutputUnit = { dimension: TakeoffDimension; label: string; /** SI amount (m, m², m³) of one unit. */ si: number };

/** Estimating units a takeoff quantity can be expressed in. */
export const TAKEOFF_OUTPUT_UNITS = {
  ea: { dimension: 0, label: 'Each', si: 1 },
  lf: { dimension: 1, label: 'Linear feet', si: 0.3048 },
  in: { dimension: 1, label: 'Inches', si: 0.0254 },
  yd: { dimension: 1, label: 'Yards', si: 0.9144 },
  m: { dimension: 1, label: 'Metres', si: 1 },
  mm: { dimension: 1, label: 'Millimetres', si: 0.001 },
  sf: { dimension: 2, label: 'Square feet', si: 0.3048 ** 2 },
  sy: { dimension: 2, label: 'Square yards', si: 0.9144 ** 2 },
  sq: { dimension: 2, label: 'Roofing squares (100 sf)', si: 100 * 0.3048 ** 2 },
  m2: { dimension: 2, label: 'Square metres', si: 1 },
  cf: { dimension: 3, label: 'Cubic feet', si: 0.3048 ** 3 },
  cy: { dimension: 3, label: 'Cubic yards', si: 0.9144 ** 3 },
  m3: { dimension: 3, label: 'Cubic metres', si: 1 },
} as const satisfies Record<string, OutputUnit>;
export type TakeoffOutputUnit = keyof typeof TAKEOFF_OUTPUT_UNITS;

export function isTakeoffOutputUnit(unit: string): unit is TakeoffOutputUnit {
  return Object.prototype.hasOwnProperty.call(TAKEOFF_OUTPUT_UNITS, unit);
}

/**
 * What a measurement measures: counts stay counts (a multiplier is "per item"); a multiplier turns
 * a length into an area (x wall height) and an area into a volume (x depth).
 */
export function takeoffDimension(kind: TakeoffKind, multiplier?: number | null): TakeoffDimension {
  if (kind === 'count') return 0;
  const base = kind === 'linear' ? 1 : 2;
  return (multiplier != null ? base + 1 : base) as TakeoffDimension;
}

/** Output units that fit a dimension, in the order a picker shows them. */
export function outputUnitsFor(dimension: TakeoffDimension): TakeoffOutputUnit[] {
  return (Object.keys(TAKEOFF_OUTPUT_UNITS) as TakeoffOutputUnit[]).filter((u) => TAKEOFF_OUTPUT_UNITS[u].dimension === dimension);
}

const isMetric = (unit: TakeoffUnit | null | undefined) => unit === 'm' || unit === 'mm';

/** The usual estimating unit: lf / sf / cy imperial, m / m2 / m3 metric, ea for counts. */
export function defaultOutputUnit(kind: TakeoffKind, scaleUnit: TakeoffUnit | null | undefined, multiplier?: number | null): TakeoffOutputUnit {
  const dimension = takeoffDimension(kind, multiplier);
  const metric = isMetric(scaleUnit);
  switch (dimension) {
    case 0:
      return 'ea';
    case 1:
      return metric ? 'm' : 'lf';
    case 2:
      return metric ? 'm2' : 'sf';
    case 3:
      return metric ? 'm3' : 'cy';
  }
}

/** The requested unit when it fits the measurement, else the default one. */
export function resolveOutputUnit(kind: TakeoffKind, scaleUnit: TakeoffUnit | null | undefined, multiplier: number | null | undefined, requested?: string | null): TakeoffOutputUnit {
  const dimension = takeoffDimension(kind, multiplier);
  if (requested && isTakeoffOutputUnit(requested) && TAKEOFF_OUTPUT_UNITS[requested].dimension === dimension) return requested;
  return defaultOutputUnit(kind, scaleUnit, multiplier);
}

/** Convert an amount between two output units of the same dimension. */
export function convertQuantity(value: number, from: TakeoffOutputUnit, to: TakeoffOutputUnit): number {
  const a = TAKEOFF_OUTPUT_UNITS[from];
  const b = TAKEOFF_OUTPUT_UNITS[to];
  if (a.dimension !== b.dimension) throw new Error(`Cannot convert ${from} to ${to}`);
  return (value * a.si) / b.si;
}

// ---------------------------------------------------------------------------
// Quantities
// ---------------------------------------------------------------------------

/** Round half away from zero to `decimals` places, robust to binary float noise (1.005 -> 1.01). */
export function roundQuantity(value: number, decimals = 2): number {
  if (!Number.isFinite(value)) return 0;
  const f = 10 ** decimals;
  const sign = value < 0 ? -1 : 1;
  return (sign * Math.round(Math.abs(value) * f * (1 + Number.EPSILON))) / f;
}

/** Apply waste in basis points: 1000 bps = +10%. */
export function applyWaste(value: number, wasteBps: number): number {
  return (value * (10_000 + wasteBps)) / 10_000;
}

export type TakeoffQuantityInput = {
  kind: TakeoffKind;
  geometry: TakeoffShapes;
  scale: TakeoffScale | null | undefined;
  /** Wall height (linear -> area), depth (area -> volume) in the scale's unit, or items per count. */
  multiplier?: number | null;
  wasteBps?: number;
  /** Requested output unit; falls back to the default when it does not fit. */
  unit?: string | null;
  /** Decimal places for lengths/areas/volumes (counts are whole). */
  decimals?: number;
};

export type TakeoffQuantity = {
  /** Rounded quantity with waste, in `unit`. */
  quantity: number;
  /** Before waste and rounding. */
  net: number;
  unit: TakeoffOutputUnit;
  dimension: TakeoffDimension;
  /** Length or area needs a scale; without one the quantity is 0. */
  needsScale: boolean;
};

/**
 * The quantity of a measurement. Lengths and areas convert pixels with the sheet scale
 * (area uses unitsPerPixel²); a multiplier is in the scale's own unit; waste is added last; counts
 * are rounded up to whole items, everything else to `decimals` (2) places.
 */
export function computeTakeoffQuantity(input: TakeoffQuantityInput): TakeoffQuantity {
  const multiplier = input.multiplier ?? null;
  const unit = resolveOutputUnit(input.kind, input.scale?.unit, multiplier, input.unit);
  const dimension = TAKEOFF_OUTPUT_UNITS[unit].dimension;
  const px = geometryPixels(input.kind, input.geometry);
  let si: number;
  if (input.kind === 'count') {
    si = px * (multiplier ?? 1);
  } else {
    if (!input.scale) return { quantity: 0, net: 0, unit, dimension, needsScale: true };
    const metresPerPx = input.scale.unitsPerPixel * METERS_PER_UNIT[input.scale.unit];
    si = input.kind === 'linear' ? px * metresPerPx : px * metresPerPx * metresPerPx;
    if (multiplier !== null) si *= multiplier * METERS_PER_UNIT[input.scale.unit];
  }
  const net = si / TAKEOFF_OUTPUT_UNITS[unit].si;
  const gross = applyWaste(net, input.wasteBps ?? 0);
  const quantity = input.kind === 'count' ? Math.ceil(roundQuantity(gross, 6)) : roundQuantity(gross, input.decimals ?? 2);
  return { quantity, net, unit, dimension, needsScale: false };
}

// ---------------------------------------------------------------------------
// Scale: calibration and standard scales
// ---------------------------------------------------------------------------

/**
 * Scale from two points a known real distance apart (draw over a dimension string). Throws when
 * the points are on top of each other or the length is not positive.
 */
export function calibrateScale(a: TakeoffPoint, b: TakeoffPoint, realLength: number, unit: TakeoffUnit): TakeoffScale {
  const px = dist(a, b);
  if (!(px >= 1)) throw new Error('Pick two points further apart');
  if (!(realLength > 0) || !Number.isFinite(realLength)) throw new Error('Enter the real length');
  return { unitsPerPixel: realLength / px, unit, label: `Calibrated (${formatNumber(realLength)} ${unit})` };
}

function formatNumber(n: number): string {
  return String(Number(n.toFixed(4)));
}

export type ScalePresetSystem = 'architectural' | 'engineering' | 'metric';
export type ScalePreset = {
  id: string;
  label: string;
  system: ScalePresetSystem;
  /** architectural: paper inches per foot; engineering: feet per paper inch; metric: the N in 1:N. */
  ratio: number;
};

const ARCH: [string, number][] = [
  ['1/32"', 1 / 32],
  ['1/16"', 1 / 16],
  ['3/32"', 3 / 32],
  ['1/8"', 1 / 8],
  ['3/16"', 3 / 16],
  ['1/4"', 1 / 4],
  ['3/8"', 3 / 8],
  ['1/2"', 1 / 2],
  ['3/4"', 3 / 4],
  ['1"', 1],
  ['1 1/2"', 1.5],
  ['3"', 3],
];
const ENG = [10, 20, 30, 40, 50, 60, 100, 200];
const METRIC = [1, 2, 5, 10, 20, 25, 50, 75, 100, 125, 200, 250, 500, 1000, 1250, 2500];

/** Standard drawing scales: architectural (x" = 1'-0"), engineering (1" = N') and metric (1:N). */
export const SCALE_PRESETS: readonly ScalePreset[] = [
  ...ARCH.map(([label, ratio]) => ({ id: `arch:${ratio}`, label: `${label} = 1'-0"`, system: 'architectural' as const, ratio })),
  ...ENG.map((ratio) => ({ id: `eng:${ratio}`, label: `1" = ${ratio}'`, system: 'engineering' as const, ratio })),
  ...METRIC.map((ratio) => ({ id: `metric:${ratio}`, label: `1:${ratio}`, system: 'metric' as const, ratio })),
];

/**
 * A standard scale as a sheet scale. `pixelsPerInch` is the render resolution at scale 1: 72 for
 * PDF pages (points), the image DPI for scanned images. Architectural and engineering scales are
 * in feet, metric ones in metres.
 */
export function scaleFromPreset(preset: ScalePreset, pixelsPerInch = PDF_POINTS_PER_INCH): TakeoffScale {
  if (!(pixelsPerInch > 0)) throw new Error('Render resolution must be positive');
  switch (preset.system) {
    case 'architectural':
      // `ratio` paper inches = 1 ft, so one paper inch is 1/ratio ft.
      return { unitsPerPixel: 1 / (preset.ratio * pixelsPerInch), unit: 'ft', label: preset.label };
    case 'engineering':
      return { unitsPerPixel: preset.ratio / pixelsPerInch, unit: 'ft', label: preset.label };
    case 'metric':
      // One paper inch is 0.0254 m of paper, `ratio` times that in the real world.
      return { unitsPerPixel: (preset.ratio * 0.0254) / pixelsPerInch, unit: 'm', label: preset.label };
  }
}

export function scalePresetById(id: string): ScalePreset | undefined {
  return SCALE_PRESETS.find((p) => p.id === id);
}

/** Real length of a pixel distance at a scale (for the calibrate preview and dimension labels). */
export function pixelsToLength(px: number, scale: TakeoffScale): number {
  return px * scale.unitsPerPixel;
}

// ---------------------------------------------------------------------------
// Summaries and estimate lines
// ---------------------------------------------------------------------------

export type TakeoffSummaryRow = { costCode: string | null; unit: string; quantity: number; measurements: number };

/** Quantities rolled up by cost code and unit (one row per code + unit), codes first, then uncoded. */
export function summarizeTakeoff(measurements: readonly { costCode?: string | null; unit: string; quantity: number }[]): TakeoffSummaryRow[] {
  const groups = new Map<string, TakeoffSummaryRow>();
  for (const m of measurements) {
    const code = m.costCode?.trim() || null;
    const key = `${code ?? ''}\u0000${m.unit}`;
    const g = groups.get(key) ?? { costCode: code, unit: m.unit, quantity: 0, measurements: 0 };
    g.quantity += m.quantity;
    g.measurements += 1;
    groups.set(key, g);
  }
  return [...groups.values()]
    .map((g) => ({ ...g, quantity: roundQuantity(g.quantity, 4) }))
    .sort((a, b) => (a.costCode ?? '￿').localeCompare(b.costCode ?? '￿') || a.unit.localeCompare(b.unit));
}

/** Default measurement colours, picked in turn as measurements are added. */
export const TAKEOFF_COLORS = ['#2563eb', '#dc2626', '#16a34a', '#d97706', '#7c3aed', '#0891b2', '#db2777', '#4b5563'] as const;
